import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import API from "../../Network/API";
import { toast } from "react-hot-toast";
import "./AgentPage.css";

function SignalOrb({ live }) {
  const canvasRef = useRef(null);
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    const dots = Array.from({ length: 78 }, () => ({
      ang: Math.random() * Math.PI * 2,
      rad: 0.06 + Math.random() * 0.3,
      spin: (Math.random() - 0.5) * 0.005,
      wobble: Math.random() * Math.PI * 2,
      size: 1.1 + Math.random() * 1.7,
    }));

    const draw = (time) => {
      const parent = canvas.parentElement;
      const box = Math.max(260, Math.min(parent.clientWidth, parent.clientHeight) - 12);
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== Math.floor(box * dpr)) {
        canvas.width = Math.floor(box * dpr);
        canvas.height = Math.floor(box * dpr);
        canvas.style.width = `${box}px`;
        canvas.style.height = `${box}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cx = box / 2;
      const cy = box / 2;
      ctx.clearRect(0, 0, box, box);

      const glow = ctx.createRadialGradient(cx, cy, 8, cx, cy, box * 0.3);
      glow.addColorStop(0, "rgba(37, 99, 235, 0.55)");
      glow.addColorStop(1, "rgba(37, 99, 235, 0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, box * 0.3, 0, Math.PI * 2);
      ctx.fill();

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(time / 9000);
      const outer = box * 0.46;
      ctx.strokeStyle = "rgba(56, 189, 248, 0.55)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, outer - 28, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, box * 0.33, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(125, 211, 252, 0.28)";
      ctx.stroke();
      for (let i = 0; i < 80; i += 1) {
        const angle = (i / 80) * Math.PI * 2;
        const long = i % 8 === 0;
        ctx.strokeStyle = long ? "rgba(186, 230, 253, 0.95)" : "rgba(56, 189, 248, 0.4)";
        ctx.lineWidth = long ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(Math.cos(angle) * (outer - (long ? 16 : 8)), Math.sin(angle) * (outer - (long ? 16 : 8)));
        ctx.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
        ctx.stroke();
        if (i % 4 === 0) {
          ctx.fillStyle = "rgba(224, 242, 254, 0.9)";
          ctx.beginPath();
          ctx.arc(Math.cos(angle) * (outer - 24), Math.sin(angle) * (outer - 24), 2.1, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();

      const speed = liveRef.current ? 2.2 : 1;
      const pts = dots.map((dot) => {
        dot.ang += dot.spin * speed;
        const wob = Math.sin(time / 800 + dot.wobble) * 0.018;
        const radius = (dot.rad + wob) * box;
        return {
          x: cx + Math.cos(dot.ang) * radius,
          y: cy + Math.sin(dot.ang) * radius * 0.9,
          size: dot.size,
        };
      });

      for (let i = 0; i < pts.length; i += 1) {
        let first = -1;
        let firstDist = Infinity;
        let second = -1;
        let secondDist = Infinity;
        for (let j = 0; j < pts.length; j += 1) {
          if (i === j) continue;
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = dx * dx + dy * dy;
          if (dist < firstDist) {
            second = first;
            secondDist = firstDist;
            first = j;
            firstDist = dist;
          } else if (dist < secondDist) {
            second = j;
            secondDist = dist;
          }
        }
        [first, second].forEach((j) => {
          if (j < i) return;
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > box * 0.26) return;
          ctx.strokeStyle = `rgba(125, 211, 252, ${Math.max(0.12, 0.5 - dist / (box * 0.7))})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(pts[i].x, pts[i].y);
          ctx.lineTo(pts[j].x, pts[j].y);
          ctx.stroke();
        });
      }

      pts.forEach((point, index) => {
        const pulse = liveRef.current ? 0.55 + Math.sin(time / 160 + index) * 0.45 : 1;
        ctx.fillStyle = "rgba(224, 242, 254, 0.95)";
        ctx.beginPath();
        ctx.arc(point.x, point.y, point.size * pulse, 0, Math.PI * 2);
        ctx.fill();
      });

      if (liveRef.current) {
        const sweep = (time / 650) % (Math.PI * 2);
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(sweep);
        const beam = ctx.createLinearGradient(0, 0, box * 0.42, 0);
        beam.addColorStop(0, "rgba(125, 211, 252, 0)");
        beam.addColorStop(1, "rgba(125, 211, 252, 0.4)");
        ctx.fillStyle = beam;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, box * 0.42, -0.35, 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        for (let i = 0; i < 3; i += 1) {
          const step = ((time / 1200) + i / 3) % 1;
          ctx.beginPath();
          ctx.arc(cx, cy, step * box * 0.46, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(125, 211, 252, ${0.5 * (1 - step)})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="orb-canvas" />
      <span className="orb-ray up" />
      <span className="orb-ray right" />
      <span className="orb-ray down" />
      <span className="orb-ray left" />
      <div className="orb-copy"><span>AI ASSISTANT</span></div>
    </>
  );
}

function RichText({ text }) {
  const parts = String(text ?? "").split("**");
  return parts.map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part));
}

const statusText = (row) => {
  if (!row) return "";
  if (row.status === 1) return "Done";
  if (row.status === 3) return "Failed";
  if (row.status === 2) return "Processing";
  return "Queued";
};

const readStats = (body) => {
  const stats = [];
  const access = body.match(/Active access alarms:\s*(\d+)/i) || body.match(/Access Active Alarm:\s*(\d+)/i) || body.match(/সক্রিয় অ্যাক্সেস অ্যালার্ম:\s*(\d+)/u);
  const alarm = body.match(/Active RMS alarms:\s*(\d+)/i) || body.match(/RMS Active Alarm:\s*(\d+)/i) || body.match(/সক্রিয় RMS অ্যালার্ম:\s*(\d+)/u);
  const acs = body.match(/Active ACS alarms:\s*(\d+)/i);
  const online = body.match(/Online sites:\s*(\d+)/i);
  const offline = body.match(/Offline sites:\s*(\d+)/i);
  const down = body.match(/Open site-down count:\s*(\d+)/i);
  if (access) stats.push({ value: access[1], label: "Access Alarms", tone: "rose" });
  if (alarm) stats.push({ value: alarm[1], label: "Active Alarms", tone: "rose" });
  if (acs) stats.push({ value: acs[1], label: "ACS Alarms", tone: "amber" });
  if (online) stats.push({ value: online[1], label: "Sites Online", tone: "green" });
  if (offline) stats.push({ value: offline[1], label: "Sites Offline", tone: "amber" });
  if (down) stats.push({ value: down[1], label: "Site Down", tone: "rose" });
  return stats;
};

const whenLabel = (value) => {
  if (!value) return "";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.round((start - day) / 86400000);
  if (diff <= 0) return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (diff === 1) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const dayBucket = (value) => {
  const date = new Date(String(value || "").replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "Today";
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.round((start - day) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return "Last 7 days";
};

const chatIcon = (title) => {
  const text = String(title || "").toLowerCase();
  if (text.includes("alarm") || text.includes("অ্যালার্ম")) return "fa-triangle-exclamation";
  if (text.includes("lock") || text.includes("access") || text.includes("door")) return "fa-lock";
  if (text.includes("report") || text.includes("export")) return "fa-file-lines";
  if (text.includes("pg") || text.includes("generator")) return "fa-gears";
  if (text.includes("site")) return "fa-location-dot";
  if (text.includes("power") || text.includes("kw") || text.includes("energy") || text.includes("mains")) return "fa-bolt";
  return "fa-chart-column";
};

export default function AgentPage() {
  const navigate = useNavigate();
  const [sideTab, setSideTab] = useState("live");
  const [messages, setMessages] = useState([]);
  const [exportsList, setExportsList] = useState([]);
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [callState, setCallState] = useState("Ready");
  const [listening, setListening] = useState(false);
  const listRef = useRef(null);
  const callRef = useRef(null);

  const applyPayload = (payload) => {
    setMessages(payload?.messages || []);
    setExportsList(payload?.exports || []);
  };

  const load = () => {
    API.get("/v1/oauth/agent/messages")
      .then((res) => applyPayload(res.data.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const pending = exportsList.some((row) => row.status === 0 || row.status === 2);

  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(() => {
      API.get("/v1/oauth/agent/exports")
        .then((res) => {
          const next = res.data.data.exports || [];
          setExportsList(next);
          if (next.some((row) => row.status === 1)) {
            API.get("/v1/oauth/agent/messages").then((msg) => setMessages(msg.data.data.messages || []));
          }
        })
        .catch(() => {});
    }, 6000);
    return () => clearInterval(timer);
  }, [pending]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sideTab]);

  const send = (value) => {
    const message = (value ?? text).trim();
    if (!message || sending) return;
    const pendingId = `local-${Date.now()}`;
    setSending(true);
    setText("");
    setSideTab("live");
    setMessages((prev) => [...prev, {
      id: pendingId,
      role: "user",
      body: message,
      created_at: new Date().toISOString(),
    }]);
    API.post("/v1/oauth/agent/chat", { message })
      .then((res) => applyPayload(res.data.data))
      .catch((err) => {
        setMessages((prev) => prev.filter((row) => row.id !== pendingId));
        toast.error(err.response?.data?.message || "Message failed");
      })
      .finally(() => setSending(false));
  };

  const clearChat = () => {
    API.post("/v1/oauth/agent/messages/clear")
      .then((res) => {
        applyPayload(res.data.data);
        setSideTab("live");
      })
      .catch(() => toast.error("Could not clear chat"));
  };

  const clearExports = () => {
    API.post("/v1/oauth/agent/exports/clear")
      .then((res) => setExportsList(res.data.data.exports || []))
      .catch(() => toast.error("Could not clear export history"));
  };

  const download = (row) => {
    API.get(`/v1/oauth/agent/exports/${row.id}/download`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = row.file_name || "report.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      })
      .catch(() => toast.error("Download failed"));
  };

  const toggleVoice = () => {
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Speech) {
      toast.error("This browser has no speech recognition");
      return;
    }
    if (listening && callRef.current?.recognition) {
      callRef.current.recognition.stop();
      setListening(false);
      return;
    }
    const recognition = new Speech();
    recognition.lang = (navigator.language || "").startsWith("bn") ? "bn-BD" : "en-US";
    recognition.onresult = (event) => {
      const said = event.results[0][0].transcript;
      setText(said);
      send(said);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    callRef.current = { ...(callRef.current || {}), recognition };
    setListening(true);
    recognition.start();
  };

  const hangUp = () => {
    const session = callRef.current;
    session?.pc?.close();
    session?.stream?.getTracks().forEach((track) => track.stop());
    if (callRef.current) {
      callRef.current.pc = null;
      callRef.current.stream = null;
    }
    setCallState("Ready");
  };

  const startCall = async () => {
    if (callRef.current?.pc) {
      hangUp();
      return;
    }
    try {
      setCallState("Connecting");
      const res = await API.post("/v1/oauth/agent/realtime/session");
      const secret = res.data.data.client_secret;
      const modelName = res.data.data.model;
      const pc = new RTCPeerConnection();
      const audio = new Audio();
      audio.autoplay = true;
      pc.ontrack = (event) => {
        audio.srcObject = event.streams[0];
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sdp = await fetch(`https://api.openai.com/v1/realtime?model=${modelName}`, {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${secret}`,
          "Content-Type": "application/sdp",
        },
      });
      if (!sdp.ok) {
        throw new Error("Call setup failed");
      }
      await pc.setRemoteDescription({ type: "answer", sdp: await sdp.text() });
      callRef.current = { ...(callRef.current || {}), pc, stream, audio };
      setCallState("In call");
    } catch (err) {
      hangUp();
      toast.error(err.response?.data?.message || err.message || "Call failed");
    }
  };

  const exportOf = (id) => exportsList.find((row) => row.id === id);
  const authUser = useSelector((state) => state.authReducer.user);
  const profileUser = useSelector((state) => state.profileReducer.user);
  const person = profileUser?.id ? profileUser : authUser;
  const personName = person?.name || person?.user_name || person?.email || "You";
  const initials = personName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "U";

  const chats = useMemo(() => {
    const rows = [];
    messages.forEach((message, index) => {
      if (message.role !== "user") return;
      const next = messages[index + 1];
      const preview = next && next.role !== "user"
        ? String(next.body).replace(/\*\*/g, "").replace(/\s+/g, " ").trim()
        : "Waiting for a reply";
      rows.push({
        id: message.id,
        title: String(message.body).replace(/\s+/g, " ").trim(),
        preview,
        time: message.created_at,
        bucket: dayBucket(message.created_at),
      });
    });
    return rows.reverse();
  }, [messages]);

  const filtered = chats.filter((row) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return row.title.toLowerCase().includes(needle) || row.preview.toLowerCase().includes(needle);
  });
  const groups = ["Today", "Yesterday", "Last 7 days"]
    .map((label) => ({ label, rows: filtered.filter((row) => row.bucket === label) }))
    .filter((group) => group.rows.length);

  const rail = callState === "In call" ? "IN CALL" : callState === "Connecting" ? "CONNECTING" : "READY";

  return (
    <div className="agent-page">
      <div className="hud">
        <section className="hud-stage">
          <button type="button" className="hud-home" onClick={() => navigate("/")}>
            <i className="fa-solid fa-arrow-left" /> Home
          </button>
          <div className="hud-grid">
            <div className={`hud-orb ${listening || callState === "In call" ? "is-live" : ""}`}>
              <SignalOrb live={listening || callState === "In call"} />
            </div>
          </div>

          <div className="hud-rail">
            <span>CORE</span><i />
            <span>ONLINE</span><i />
            <span>SITES</span><i />
            <span>ALARMS</span><i />
            <span>LOCK</span><i />
            <span>{rail}</span>
          </div>

          <form className="hud-composer" onSubmit={(event) => { event.preventDefault(); send(); }}>
            <button type="button" className={`hud-mic ${listening ? "active" : ""}`} onClick={toggleVoice} aria-label="Microphone">
              <i className="fa-solid fa-microphone" />
            </button>
            <button type="button" className={`hud-wave ${callState === "In call" ? "active" : ""}`} onClick={startCall} aria-label="Voice call">
              <span /><span /><span /><span /><span />
            </button>
            <input
              value={text}
              placeholder="Ask about your RMS data, reports, alarms, sites..."
              onChange={(e) => setText(e.target.value)}
            />
            <button type="submit" className="hud-send" disabled={sending}>
              <i className="fa-solid fa-paper-plane" />
              {sending ? "..." : "Send"}
            </button>
          </form>
        </section>

        <aside className="hud-side">
          <div className="hud-side-top">
            <div className="hud-tabs">
              <button type="button" className={sideTab === "live" ? "active" : ""} onClick={() => setSideTab("live")}><i className="fa-regular fa-comment-dots" /> Live</button>
              <button type="button" className={sideTab === "chats" ? "active" : ""} onClick={() => setSideTab("chats")}><i className="fa-regular fa-comments" /> Chats</button>
              <button type="button" className={sideTab === "export" ? "active" : ""} onClick={() => setSideTab("export")}><i className="fa-solid fa-arrow-up-from-bracket" /> Export</button>
            </div>
            <button type="button" className="hud-new" onClick={clearChat}><i className="fa-regular fa-trash-can" /> Clear Chat</button>
          </div>

          {sideTab === "chats" && (
            <>
              <label className="hud-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input value={query} placeholder="Search conversations..." onChange={(e) => setQuery(e.target.value)} />
              </label>
              <div className="hud-scroll">
                {groups.length === 0 && <p className="hud-empty">No conversations yet.</p>}
                {groups.map((group) => (
                  <div key={group.label}>
                    <p className="hud-day">{group.label}</p>
                    {group.rows.map((row, index) => (
                      <button key={row.id} type="button" className={`hud-chat ${index === 0 && group.label === groups[0].label ? "active" : ""}`} onClick={() => setSideTab("live")}>
                        <span className="hud-chat-ico"><i className={`fa-solid ${chatIcon(row.title)}`} /></span>
                        <span>
                          <strong>{row.title.slice(0, 48)}</strong>
                          <small>{row.preview.slice(0, 72)}</small>
                        </span>
                        <em>{whenLabel(row.time)}</em>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}

          {sideTab === "live" && (
            <div className="hud-scroll hud-thread" ref={listRef}>
              {messages.length === 0 && !sending && (
                <div className="chat-agent">
                  <span className="chat-bot"><i className="fa-solid fa-robot" /></span>
                  <div className="chat-card intro-card">
                    <p>I am your AI Assistant. I can help with your RMS data.</p>
                    <ul>
                      <li>Site online and offline status</li>
                      <li>Active RMS alarms</li>
                      <li>Lock and access alarms</li>
                      <li>Site-down count</li>
                      <li>Export the same reports as the Reports page</li>
                    </ul>
                    <p className="intro-note">Ask in Bangla, Banglish, or English. I only read and export. I cannot create, edit, or delete data.</p>
                  </div>
                </div>
              )}
              {messages.map((message) => {
                const file = message.export_id ? exportOf(message.export_id) : null;
                if (message.role === "user") {
                  return (
                    <div key={message.id} className="chat-user">
                      <div>
                        <div className="chat-user-bubble"><RichText text={message.body} /></div>
                        <time>{whenLabel(message.created_at)}</time>
                      </div>
                      <span className="chat-avatar">{initials}</span>
                    </div>
                  );
                }
                const stats = readStats(message.body);
                return (
                  <div key={message.id} className="chat-agent">
                    <span className="chat-bot"><i className="fa-solid fa-robot" /></span>
                    <div className="chat-card">
                      <p><RichText text={message.body} /></p>
                      {stats.length > 0 && (
                        <div className="chat-stats">
                          {stats.map((item) => (
                            <div key={item.label} className={`chat-stat ${item.tone}`}>
                              <strong>{item.value}</strong>
                              <span>{item.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {file && (
                        <div className="chat-file">
                          <span className="chat-file-copy">
                            <strong>{file.file_name || "Report"}</strong>
                            <small>{file.status === 1 ? "Ready to download" : `${statusText(file)} · ${file.percent || 0}%`}</small>
                          </span>
                          {file.status === 1 ? (
                            <button type="button" className="chat-download" onClick={() => download(file)}>Download</button>
                          ) : (
                            <div className="agent-bar"><span style={{ width: `${file.percent || 5}%` }} /></div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {sending && <div className="chat-agent"><span className="chat-bot"><i className="fa-solid fa-robot" /></span><div className="chat-card"><p>Typing...</p></div></div>}
            </div>
          )}

          {sideTab === "export" && (
            <div className="hud-scroll">
              <div className="export-head">
                <span>Last 2 days</span>
                <button type="button" className="hud-new" onClick={clearExports} disabled={!exportsList.length}>
                  <i className="fa-regular fa-trash-can" /> Clear history
                </button>
              </div>
              {exportsList.length === 0 && <p className="hud-empty">No exports in the last 2 days.</p>}
              {exportsList.map((row) => (
                <div key={row.id} className="hud-chat export-row">
                  <span className="hud-chat-ico"><i className="fa-regular fa-file-lines" /></span>
                  <span>
                    <strong>{row.file_name || row.report_type}</strong>
                    <small>{statusText(row)} · {row.percent || 0}%</small>
                  </span>
                  {row.status === 1 ? (
                    <button type="button" className="chat-download" onClick={() => download(row)}>Download</button>
                  ) : (
                    <div className="agent-bar"><span style={{ width: `${row.status === 3 ? 0 : row.percent || 0}%` }} /></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
